import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { inr, formatDate, statusColor } from "@/lib/format";

const STATUSES = ["requested", "confirmed", "payment_pending", "paid", "delivered", "active", "return_requested", "returned", "completed", "cancelled", "overdue"];

export default function TenantOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");

  const refresh = () => api.get("/tenant/orders").then(({ data }) => setOrders(data));
  useEffect(() => { refresh(); }, []);

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const update = async () => {
    try {
      await api.put(`/tenant/orders/${open.id}/status`, { status: newStatus, note });
      toast.success("Updated");
      setOpen(null); setNote(""); setNewStatus("");
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="tenant-orders-title">Rental Orders</h1>
          <p className="text-slate-500 text-sm mt-1">{filtered.length} orders</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="order-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="p-0 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Order</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Items</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Payment</th>
              <th className="text-right px-4 py-3">Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">No orders</td></tr>}
            {filtered.map((o) => (
              <tr key={o.id} className="border-t border-slate-100" data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-3 font-mono text-xs">#{o.id.slice(0, 8)}</td>
                <td className="px-4 py-3 text-xs">{o.customer?.name}<br /><span className="text-slate-400">{o.customer?.phone}</span></td>
                <td className="px-4 py-3 text-xs">{o.items?.length} item(s)</td>
                <td className="px-4 py-3 text-xs">{formatDate(o.created_at)}</td>
                <td className="px-4 py-3"><span className={`pill ${statusColor(o.status)}`}>{o.status}</span></td>
                <td className="px-4 py-3"><span className={`pill ${statusColor(o.payment_status)}`}>{o.payment_status}</span></td>
                <td className="px-4 py-3 text-right font-semibold">{inr(o.total)}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => { setOpen(o); setNewStatus(o.status); }} data-testid={`order-update-${o.id}`}>Update</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update Order #{open?.id?.slice(0, 8)}</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-md text-sm">
                <div className="font-medium">{open.customer?.name} • {open.customer?.phone}</div>
                <div className="text-slate-600 mt-1">{open.items?.map((i) => `${i.product_name} × ${i.qty}`).join(", ")}</div>
                <div className="font-semibold mt-1">{inr(open.total)} • {open.payment_status}</div>
              </div>
              <div>
                <Label>New status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="order-new-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Note (optional)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="order-update-note" /></div>
              <Button className="w-full" onClick={update} data-testid="order-update-save">Save Status</Button>
              {open.timeline?.length > 0 && (
                <div>
                  <Label className="block mb-2">Timeline</Label>
                  <div className="space-y-1 text-xs max-h-32 overflow-y-auto">
                    {[...open.timeline].reverse().map((t, i) => (
                      <div key={i} className="border-l-2 border-slate-200 pl-2">{t.status} – {t.note} <span className="text-slate-400">({formatDate(t.at)})</span></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
